<template>
  <div class="feedback-form space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-bold text-gray-800">📝 Feedback du Jour</h2>
      <button @click="close" class="p-2 hover:bg-gray-100 rounded-lg">
        <span class="text-2xl">✕</span>
      </button>
    </div>

    <!-- Effectifs -->
    <div class="bg-blue-50 rounded-xl p-4">
      <p class="text-sm text-gray-600 mb-2">Lundi 5 janvier - Déjeuner</p>
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-blue-600">{{ expected }}</div>
          <div class="text-xs text-gray-600">Prévus</div>
        </div>
        <div class="bg-white rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-green-600">{{ present }}</div>
          <div class="text-xs text-gray-600">Présents</div>
        </div>
      </div>
    </div>

    <!-- Appréciation générale -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <h3 class="font-bold text-gray-800 mb-3">😊 Appréciation générale</h3>
      <div class="flex justify-around">
        <button
          v-for="(emoji, idx) in emojis"
          :key="idx"
          @click="globalRating = idx + 1"
          :class="{ 'scale-125': globalRating === idx + 1, 'opacity-50': globalRating !== idx + 1 }"
          class="text-4xl transition"
        >
          {{ emoji }}
        </button>
      </div>
    </div>

    <!-- Détail par plat -->
    <div class="space-y-3">
      <h3 class="font-bold text-gray-800">🍽️ DÉTAIL PAR PLAT</h3>

      <div v-for="(dish, idx) in dishes" :key="idx" class="bg-white rounded-xl p-4 shadow-sm">
        <div class="flex justify-between items-start mb-3">
          <div>
            <h4 class="font-semibold text-gray-800">{{ dish.name }}</h4>
            <p class="text-xs text-gray-500">{{ dish.category }}</p>
          </div>
        </div>

        <!-- Appréciation -->
        <div class="mb-3">
          <p class="text-xs text-gray-600 mb-1">Appréciation</p>
          <div class="flex gap-2">
            <button
              v-for="(emoji, i) in emojis"
              :key="i"
              @click="updateDishRating(idx, i + 1)"
              :class="{ 'scale-110': dish.rating === i + 1, 'opacity-30': dish.rating !== i + 1 }"
              class="text-2xl transition"
            >
              {{ emoji }}
            </button>
          </div>
        </div>

        <!-- Quantités -->
        <!-- <div class="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p class="text-xs text-gray-600 mb-1">Servi</p>
            <input type="range" v-model="dish.served" min="0" max="100" class="w-full" />
            <p class="text-xs text-gray-600 mt-1">{{ dish.served }}%</p>
          </div>
          <div>
            <p class="text-xs text-gray-600 mb-1">Restes</p>
            <input type="range" v-model="dish.leftover" min="0" max="100" class="w-full" />
            <p class="text-xs text-gray-600 mt-1">{{ dish.leftover }}%</p>
          </div>
        </div> -->

        <!-- Commentaire par plat -->
        <div v-if="dish.comment" class="bg-yellow-50 rounded p-2">
          <p class="text-xs text-gray-700">💬 "{{ dish.comment }}"</p>
        </div>
        <button @click="editDishComment(idx)" class="text-xs text-blue-600 mt-2 hover:underline">
          {{ dish.comment ? "Modifier" : "Ajouter" }} un commentaire
        </button>
      </div>
    </div>

    <!-- Contexte -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <h3 class="font-bold text-gray-800 mb-3">🌤️ CONTEXTE</h3>

      <!-- Météo -->
      <div class="mb-4">
        <p class="text-sm text-gray-600 mb-2">Météo</p>
        <div class="flex gap-2">
          <button
            v-for="w in weatherOptions"
            :key="w.key"
            @click="weather = w.key"
            :class="{ 'border-blue-500 bg-blue-50': weather === w.key, 'border-gray-200': weather !== w.key }"
            class="flex-1 flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition"
          >
            <span class="text-2xl">{{ w.icon }}</span>
            <span class="text-xs">{{ w.label }}</span>
          </button>
        </div>
      </div>

      <!-- Température -->
      <div class="mb-4">
        <p class="text-sm text-gray-600 mb-2">Température</p>
        <div class="flex items-center gap-3">
          <input
            type="number"
            v-model="temperature"
            class="w-20 p-2 border-2 border-gray-200 rounded-lg text-center"
            placeholder="22"
          />
          <span class="text-gray-600">°C</span>
        </div>
      </div>

      <!-- Ambiance -->
      <div>
        <p class="text-sm text-gray-600 mb-2">Ambiance</p>
        <div class="flex gap-2">
          <button
            v-for="amb in ambianceOptions"
            :key="amb.key"
            @click="ambiance = amb.key"
            :class="{ 'border-blue-500 bg-blue-50': ambiance === amb.key, 'border-gray-200': ambiance !== amb.key }"
            class="flex-1 flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition"
          >
            <span class="text-2xl">{{ amb.emoji }}</span>
            <span class="text-xs">{{ amb.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Commentaires libres -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <h3 class="font-bold text-gray-800 mb-3">💬 Commentaires libres</h3>
      <textarea
        v-model="generalComment"
        placeholder="Bonne ambiance aujourd'hui..."
        class="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
        rows="4"
      />
    </div>

    <!-- Photos -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <h3 class="font-bold text-gray-800 mb-3">📸 Photos</h3>
      <div class="flex gap-2">
        <button v-for="i in photos" :key="i" class="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
          <span class="text-3xl">📷</span>
        </button>
        <button
          @click="addPhoto"
          class="w-20 h-20 bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg flex items-center justify-center hover:bg-blue-100 transition"
        >
          <span class="text-3xl text-blue-500">+</span>
        </button>
      </div>
    </div>

    <!-- Bouton d'envoi -->
    <button
      @click="submit"
      class="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-lg p-4 font-semibold shadow-md transition flex items-center justify-center gap-2"
    >
      <span>📤</span>
      Envoyer le feedback
    </button>
  </div>
</template>

<script>
export default {
  name: "FeedbackForm",
  data() {
    return {
      expected: 142,
      present: 138,
      globalRating: 4,
      emojis: ["😞", "😕", "😐", "😊", "😍"],
      dishes: [
        { name: "Taboulé", category: "Entrée", rating: 4, served: 80, leftover: 20, comment: "" },
        {
          name: "Sauté de porc* sauce pruneaux",
          category: "Plat principal",
          rating: 3,
          served: 100,
          leftover: 10,
          comment: "Morceaux un peu secs",
        },
        {
          name: "Carottes vichy",
          category: "Accompagnement",
          rating: 2,
          served: 70,
          leftover: 30,
          comment: "Portions trop importantes",
        },
        { name: "Yaourt nature BIO", category: "Produit laitier", rating: 4, served: 100, leftover: 0, comment: "" },
        { name: "Compote poire", category: "Dessert", rating: 4, served: 90, leftover: 5, comment: "" },
        {
          name: "Colin sauce pruneaux",
          category: "Sans viande",
          rating: 3,
          served: 60,
          leftover: 15,
          comment: "Alternative appréciée",
        },
      ],
      weather: "sunny",
      weatherOptions: [
        { key: "sunny", icon: "☀️", label: "Ensoleillé" },
        { key: "cloudy", icon: "☁️", label: "Nuageux" },
        { key: "rainy", icon: "🌧️", label: "Pluvieux" },
      ],
      temperature: 22,
      ambiance: "calm",
      ambianceOptions: [
        { key: "calm", emoji: "😊", label: "Calme" },
        { key: "normal", emoji: "😐", label: "Normal" },
        { key: "agitated", emoji: "😟", label: "Agité" },
      ],
      generalComment: "",
      photos: [],
    }
  },
  methods: {
    close() {
      this.$emit("close")
    },
    updateDishRating(idx, rating) {
      this.dishes[idx].rating = rating
    },
    editDishComment(idx) {
      const newComment = prompt("Commentaire pour " + this.dishes[idx].name + ":", this.dishes[idx].comment)
      if (newComment !== null) {
        this.dishes[idx].comment = newComment
      }
    },
    addPhoto() {
      this.photos.push(this.photos.length + 1)
      alert("Photo ajoutée")
    },
    submit() {
      const feedbackData = {
        expected: this.expected,
        present: this.present,
        globalRating: this.globalRating,
        dishes: this.dishes,
        weather: this.weather,
        temperature: this.temperature,
        ambiance: this.ambiance,
        generalComment: this.generalComment,
        photos: this.photos,
      }
      console.log("Feedback envoyé:", feedbackData)
      alert("Feedback envoyé avec succès !")
      this.close()
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.gap-1 {
  gap: 0.25rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.flex-col {
  flex-direction: column;
}
.items-start {
  align-items: flex-start;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.justify-around {
  justify-content: space-around;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded {
  border-radius: 0.25rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-dashed {
  border-style: dashed;
}
.border-gray-200 {
  border-color: #e5e7eb;
}
.border-blue-300 {
  border-color: #93c5fd;
}
.border-blue-500 {
  border-color: #3b82f6;
}
.bg-white {
  background-color: #ffffff;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-blue-100 {
  background-color: #dbeafe;
}
.hover\:bg-blue-100:hover {
  background-color: #dbeafe;
}
.bg-yellow-50 {
  background-color: #fefce8;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.bg-blue-600 {
  background-color: #2563eb;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.text-blue-500 {
  color: #3b82f6;
}
.text-blue-600 {
  color: #2563eb;
}
.text-green-600 {
  color: #16a34a;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.text-white {
  color: #ffffff;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.text-center {
  text-align: center;
}
.p-2 {
  padding: 0.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mt-1 {
  margin-top: 0.25rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.w-full {
  width: 100%;
}
.w-20 {
  width: 5rem;
}
.h-20 {
  height: 5rem;
}
.opacity-30 {
  opacity: 0.3;
}
.opacity-50 {
  opacity: 0.5;
}
.scale-110 {
  transform: scale(1.1);
}
.scale-125 {
  transform: scale(1.25);
}
.resize-none {
  resize: none;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
.hover\:underline:hover {
  text-decoration: underline;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:hover {
  opacity: 0.9;
}
input[type="range"] {
  width: 100%;
}
input[type="number"],
textarea {
  font-family: inherit;
}
input:focus,
textarea:focus {
  outline: none;
}
.focus\:border-blue-500:focus {
  border-color: #3b82f6;
}
.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}
</style>
